import { Modal, Tag, InputNumber, Input, Select, Button, Checkbox } from 'antd';
import { ProfileOutlined, SaveOutlined, ThunderboltFilled } from '@ant-design/icons';
import { formatMoney, reconcileProformaTotals } from './proformaHelpers';
import ProformaSalespersonField from './ProformaSalespersonField';

// ── Stage 6Q — Proforma "Details & Summary" popup ──────────────────
// Consolidated header dialog: order-detail metadata (salesperson auto-
// mapped from the customer, project ref, templates received, P.O, and
// the dispatch / work-type / payment-terms selectors) alongside the live
// financial statement from the preview engine (with the editable document
// discount). Replaces the inline salesperson/project strip and the
// bottom-panel "Summary" tab.
//
// Force-rendered so the salesperson auto-mapping effect runs on load even
// while the popup is closed (salesperson is mandatory to save, so it must
// resolve the moment a customer is chosen).

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const SOON = 'Options are configured in a later release.';

// One labelled control on a fixed rail so every field aligns.
function Field({ label, required, children, start }) {
  return (
    <div className={`flex ${start ? 'items-start' : 'items-center'} gap-2.5`}>
      <span className={`pf-od-label ${start ? 'pt-[5px]' : ''}`}>
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// A sub-group of fields under a quiet uppercase header.
function Group({ title, children }) {
  return (
    <div className="mt-3">
      <div className="pf-od-sub">{title}</div>
      <div className="mt-1.5 flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

// One row of the summary build-up grid: Label · % · Rs · Amount. `variant`
// adds emphasis (strong sub-totals, the final grand total). The Discount row
// supplies its own editable % / Rs cells, so it is rendered inline instead.
function SumRow({ label, amount, variant, subnote }) {
  const cls = variant === 'grand' ? 'pf-sum-grand' : variant === 'strong' ? 'pf-sum-strong' : '';
  return (
    <tr className={cls}>
      <td className="pf-sum-lab">
        {label}
        {subnote ? <span className="pf-sum-sub">{subnote}</span> : null}
      </td>
      <td className="pf-sum-pct" />
      <td className="pf-sum-rs" />
      <td className="pf-sum-amt">{amount}</td>
    </tr>
  );
}

export default function ProformaSummaryModal({
  open, onClose, header, setHeader, setHeaderRaw, markDirty, readOnly, totals, supplyType, otherCharges,
  onSave, saving
}) {
  const t = totals || {};
  const ro = !!readOnly;
  const intra = supplyType === 'intra_state';
  const expressOn = !!(header && header.expressDelivery);

  // ── Live auto Other-Charges reconciliation (display only) ──
  // Delegated to the shared reconcileProformaTotals so this popup and the
  // bottom-panel "running total" can never disagree — they consume the SAME
  // helper. It adds only the live-vs-persisted OC delta (no double-count on a
  // reloaded doc) and folds the delta's GST by supply type. On a saved doc the
  // delta is 0, so the figures equal the persisted / printed total.
  const oc = otherCharges || { amount: 0, gst: 0, ready: false };
  // Pass the document round-off policy so the grand total re-derives round-off on
  // the FINAL (base + live OC) total — matching the backend/print rupee rather than
  // appending the OC delta to the base's pre-OC round-off (first-load ₹…​.14 bug).
  const r = reconcileProformaTotals(t, oc, supplyType,
    { roundOffEnabled: header && header.roundOffEnabled, roundOffMode: header && header.roundOffMode });
  const ocAmount = r.otherChargeAmount;                 // OC reflected in the total
  const taxable  = r.taxableAmount;
  const cgst = r.cgstAmount, sgst = r.sgstAmount, igst = r.igstAmount;
  const grand = r.grandTotal;
  // Round-off is re-derived by reconcile on the final (base + live OC) total, so it
  // stays consistent with `grand` — reading the base t.roundOffAmount would show the
  // pre-OC round-off (e.g. 0.29) beside a grand total rounded to 0.16.
  const roundOffDisp = r.roundOffAmount;

  // Spreadsheet build-up figures (money only). Scrap is already folded into
  // basicAmount by the preview engine, and Manual Charges are shown inside the
  // Sub Total per the approved layout; Sub Total − Discount = Grand Sub Total =
  // the engine's taxable value, so the whole column reconciles to Grand Total.
  const r2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;
  const manualAmt = num(t.manualChargesAmount);
  const subTotalDisp = r2(num(t.basicAmount) + num(t.valueAddedAmount) + num(t.fabricationAmount) + ocAmount + manualAmt);
  const discAmt = num(t.docDiscountAmount);
  const grandSubTotal = taxable;   // = subTotalDisp − discAmt; drives the GST split

  const setH = (patch) => setHeader((h) => ({ ...h, ...patch }));
  const setDocDiscPct = (v) => setH({ docDiscountPercent: Math.max(0, Math.min(100, num(v))), docDiscountAmount: 0 });
  const setDocDiscAmt = (v) => setH({ docDiscountPercent: 0, docDiscountAmount: Math.max(0, num(v)) });

  const soonSelect = (field, placeholder) => (
    <Select
      size="small" className="w-full" allowClear showSearch optionFilterProp="label"
      disabled={ro} options={[]} value={header?.[field] || undefined}
      placeholder={placeholder} notFoundContent={<span className="text-[11px] text-slate-400">{SOON}</span>}
      onChange={(v) => setH({ [field]: v || null })}
    />
  );
  const textField = (field, placeholder) => (
    <Input size="small" disabled={ro} placeholder={placeholder}
      value={header?.[field] || ''} onChange={(e) => setH({ [field]: e.target.value })} />
  );

  return (
    <Modal
      open={open} onCancel={onClose} forceRender destroyOnClose={false}
      width={940}
      styles={{ body: { paddingTop: 2 } }}
      footer={
        // Stage SALES-PROFORMA-UI-FLOW-4 — Save PI / Update now open this window
        // as a review-and-confirm step. The confirmation buttons live here: "No"
        // closes without saving; the primary button runs the same save as before
        // (validation surfaces here, next to the salesperson field). When no
        // onSave handler is supplied the window falls back to a plain "Done".
        onSave
          ? [
              <Button key="no" onClick={onClose}>No</Button>,
              <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
                {header?.id ? 'Update' : 'Save PI'}
              </Button>,
            ]
          : [<Button key="done" type="primary" onClick={onClose}>Done</Button>]
      }
      title={
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-50 text-[#1877F2]">
            <ProfileOutlined style={{ fontSize: 13 }} />
          </span>
          <span className="text-[15px] font-semibold text-slate-800">Order Details &amp; Summary</span>
        </span>
      }
    >
      <style>{`
.pf-od-label{font-size:12px;color:#64748b;flex-shrink:0;width:100px;}
.pf-od-eyebrow{font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#1877F2;}
.pf-od-sub{font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#94a3b8;border-bottom:1px solid #eef2f7;padding-bottom:3px;}
/* Underline field style, matching the Bill To / Ship To customer section
   (.pf-cust) — transparent background, single bottom border, brand focus. */
.pf-od .ant-input{border:0;border-bottom:1px solid #cbd5e1;border-radius:0;padding:2px 2px;font-size:12.5px;background:transparent;color:#1e293b;transition:border-color .15s ease;}
.pf-od .ant-input::placeholder{color:#cbd5e1;font-weight:400;}
.pf-od .ant-input:hover{border-bottom-color:#94a3b8;}
.pf-od .ant-input:focus,.pf-od .ant-input-focused{box-shadow:none;border-bottom-color:#1877F2;}
.pf-od .ant-input[disabled]{background:transparent;color:#475569;border-bottom-style:dotted;}
.pf-od .ant-select .ant-select-selector{border:0!important;border-bottom:1px solid #cbd5e1!important;border-radius:0!important;box-shadow:none!important;padding:0 2px!important;background:transparent!important;transition:border-color .15s ease;}
.pf-od .ant-select:hover .ant-select-selector{border-bottom-color:#94a3b8!important;}
.pf-od .ant-select-focused .ant-select-selector{border-bottom-color:#1877F2!important;}
.pf-od .ant-select-selection-placeholder{color:#cbd5e1;font-size:12.5px;}
.pf-od .ant-select-selection-item,.pf-od .ant-select-selection-search-input{font-size:12.5px;color:#1e293b;}
/* Express Delivery — prominent priority toggle (bigger, bolder). Enlarged
   checkbox, a lightning glyph, and a strong label; turns amber when ON. */
.pf-express{display:inline-flex;align-items:center;gap:10px;margin-top:8px;padding:7px 12px;border:1px solid #e2e8f0;border-radius:9px;background:#f8fafc;cursor:pointer;transition:background .15s,border-color .15s;}
.pf-express:hover{border-color:#cbd5e1;}
.pf-express.is-on{background:#fffbeb;border-color:#fde68a;}
.pf-express.is-ro{cursor:default;opacity:.85;}
.pf-express-chk .ant-checkbox-inner{width:20px;height:20px;}
.pf-express-chk .ant-checkbox-inner::after{inset-inline-start:5.5px;width:6px;height:11px;}
.pf-express-ico{font-size:16px;color:#cbd5e1;transition:color .15s;}
.pf-express.is-on .pf-express-ico{color:#f59e0b;}
.pf-express-lab{font-size:15px;font-weight:700;color:#475569;letter-spacing:.01em;}
.pf-express.is-on .pf-express-lab{color:#b45309;}
.pf-od-disc .ant-input-number{width:96px;background:#fff7e6;border:1px solid #fde3b3;border-radius:6px;}
.pf-od-disc .ant-input-number:hover{border-color:#f5c877;}
.pf-od-disc .ant-input-number-focused{border-color:#e8973a;box-shadow:0 0 0 2px rgba(232,151,58,.15);}
.pf-od-disc .ant-input-number-input{height:22px;text-align:right;font-size:12px;font-variant-numeric:tabular-nums;color:#b45309;}
.pf-od-disc .ant-input-number-prefix{color:#d08329;}
/* Summary build-up grid — Label · % · Rs · Amount. Header band toned to the
   AntD neutral (was a bright spreadsheet yellow), light borders to match the
   app's table language. */
.pf-sum{border-collapse:collapse;width:100%;font-size:12.5px;table-layout:fixed;}
.pf-sum col.pf-sum-pct{width:60px;} .pf-sum col.pf-sum-rs{width:74px;} .pf-sum col.pf-sum-amt{width:120px;}
.pf-sum thead td{background:#f1f5f9;height:10px;border:1px solid #e2e8f0;padding:0;}
.pf-sum tbody td{border:1px solid #eef2f7;padding:6px 10px;color:#334155;vertical-align:middle;}
.pf-sum .pf-sum-lab{text-align:left;}
.pf-sum-sub{color:#94a3b8;font-size:10px;font-weight:400;margin-left:6px;}
.pf-sum .pf-sum-pct,.pf-sum .pf-sum-rs{text-align:center;color:#64748b;padding:2px 4px;}
.pf-sum .pf-sum-amt{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap;color:#1e293b;}
.pf-sum tr.pf-sum-strong td{background:#f8fafc;font-weight:600;}
.pf-sum tr.pf-sum-strong .pf-sum-amt{font-weight:700;color:#0f172a;}
.pf-sum tr.pf-sum-grand td{background:#ecfdf5;border-color:#a7f3d0;border-top:2px solid #6ee7b7;}
.pf-sum tr.pf-sum-grand .pf-sum-lab{font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#047857;font-size:12px;}
.pf-sum tr.pf-sum-grand .pf-sum-amt{font-weight:800;font-size:16px;color:#047857;}
.pf-sum tr.pf-sum-disc .pf-sum-disc-amt{color:#b45309;}
.pf-sum tr.pf-sum-disc .ant-input-number{width:100%;background:#fff7e6;border:1px solid #fde3b3;border-radius:6px;}
.pf-sum tr.pf-sum-disc .ant-input-number:hover{border-color:#f5c877;}
.pf-sum tr.pf-sum-disc .ant-input-number-focused{border-color:#e8973a;box-shadow:0 0 0 2px rgba(232,151,58,.15);}
.pf-sum tr.pf-sum-disc .ant-input-number-input{height:22px;text-align:right;font-size:11.5px;font-variant-numeric:tabular-nums;color:#b45309;}
.pf-sum tr.pf-sum-disc .ant-input-number-prefix{color:#d08329;}
      `}</style>

      <div className="pf-od flex flex-wrap gap-x-7 gap-y-4">
        {/* ── ORDER DETAILS ─────────────────────────────────────── */}
        <div className="flex-1 min-w-[370px]">
          <div className="pf-od-eyebrow mb-2">Order Details</div>

          {/* Salesperson — the mandatory, auto-mapped hero field. */}
          <div className="rounded-lg border border-blue-100 bg-blue-50/40 px-3 py-2">
            <Field label="Salesperson" required start>
              <ProformaSalespersonField header={header} setHeader={setHeaderRaw || setHeader}
                onUserEdit={markDirty} readOnly={readOnly} bare />
            </Field>
            <div className="text-[11px] text-slate-400 mt-1 ml-[110px] leading-snug">
              Auto-filled from the selected customer. An override needs a reason.
            </div>
          </div>

          <Group title="References">
            <Field label="Project Ref">{textField('projectName', 'Project name / reference')}</Field>
            <Field label="Templates Rcvd">{textField('templatesReceived', 'Templates received')}</Field>
            <Field label="P.O No">{textField('poNo', 'Purchase order number')}</Field>
            <Field label="P.O Date">{textField('poDate', 'e.g. 12/07/2026')}</Field>
          </Group>

          <Group title="Logistics & Terms">
            <Field label="Dispatch Route">{soonSelect('dispatchRoute', 'Select route…')}</Field>
            <Field label="Dispatch Mode">{soonSelect('dispatchMode', 'Select mode…')}</Field>
            <Field label="Work Type">{soonSelect('workType', 'Select work type…')}</Field>
            <Field label="Payment Terms">{soonSelect('paymentTerms', 'Select payment terms…')}</Field>

            {/* Express Delivery — a prominent priority toggle. When ON, the
                order is flagged with a gold star on the proforma list. Persists
                with the document (rehydrates on edit). */}
            <label className={`pf-express ${expressOn ? 'is-on' : ''} ${ro ? 'is-ro' : ''}`}>
              <Checkbox
                disabled={ro}
                checked={expressOn}
                onChange={(e) => setH({ expressDelivery: e.target.checked })}
                className="pf-express-chk"
              />
              <ThunderboltFilled className="pf-express-ico" />
              <span className="pf-express-lab">Express Delivery</span>
            </label>
          </Group>
        </div>

        {/* ── SUMMARY ───────────────────────────────────────────── */}
        <div className="flex-1 min-w-[320px]">
          <div className="flex items-center justify-between mb-2">
            <span className="pf-od-eyebrow">Summary</span>
            <span className="inline-flex items-center gap-2">
              <Tag color={intra ? 'green' : supplyType === 'inter_state' ? 'blue' : 'orange'} style={{ marginInlineEnd: 0 }}>
                {(supplyType || 'intra_state').replace('_', ' ').toUpperCase()}
              </Tag>
              <span className="text-[11px] text-slate-400">
                {intra ? 'CGST + SGST' : supplyType === 'inter_state' ? 'IGST' : 'Zero-rated'}
              </span>
            </span>
          </div>

          {/* Document metrics (non-money context; area at 3 dp per CLAUDE.md 5A). */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 mb-2">
            <span>Lines <b className="text-slate-700">{t.lineCount || 0}</b></span>
            <span>Qty <b className="text-slate-700">{t.totalQty || 0}</b></span>
            <span>Charge SQMT <b className="text-slate-700">{(t.totalChargeSqm || 0).toFixed(3)}</b></span>
            <span>Charge SQFT <b className="text-slate-700">{(t.totalChargeSqft || 0).toFixed(3)}</b></span>
          </div>

          {/* Build-up grid — Label · % · Rs · Amount. Sub Total = Basic + Value
              Added + Fabrication + Other Charges + Manual; − Discount = Grand Sub
              Total (= taxable); then the GST split → one Grand Total. */}
          <table className="pf-sum">
            <colgroup>
              <col /><col className="pf-sum-pct" /><col className="pf-sum-rs" /><col className="pf-sum-amt" />
            </colgroup>
            <thead><tr><td /><td /><td /><td /></tr></thead>
            <tbody>
              <SumRow label="Basic" amount={formatMoney(t.basicAmount)}
                subnote={t.scrapAmount ? `incl. scrap ${formatMoney(t.scrapAmount)}` : null} />
              <SumRow label="Value Added" amount={formatMoney(t.valueAddedAmount)} />
              <SumRow label="Fabrication" amount={formatMoney(t.fabricationAmount)} />
              <SumRow label="Other Charges" amount={formatMoney(ocAmount)} />
              <SumRow label="Manual Charges" amount={formatMoney(manualAmt)} />
              <SumRow label="Sub Total" amount={formatMoney(subTotalDisp)} variant="strong" />

              {/* Editable document discount — enter a % or a flat Rs. */}
              <tr className="pf-sum-disc">
                <td className="pf-sum-lab">Discount</td>
                <td className="pf-sum-pct">
                  <InputNumber
                    size="small" min={0} max={100} step={0.5} controls={false}
                    disabled={ro} value={num(header && header.docDiscountPercent) || null}
                    formatter={(v) => (v == null || v === '' ? '' : `${v}%`)}
                    parser={(v) => String(v == null ? '' : v).replace(/[%\s]/g, '')}
                    onChange={setDocDiscPct} placeholder="0%" />
                </td>
                <td className="pf-sum-rs">
                  <InputNumber
                    size="small" min={0} controls={false} prefix="₹"
                    disabled={ro} value={num(header && header.docDiscountAmount) || null}
                    onChange={setDocDiscAmt} placeholder="0" />
                </td>
                <td className="pf-sum-amt pf-sum-disc-amt">{discAmt ? `-${formatMoney(discAmt)}` : formatMoney(0)}</td>
              </tr>

              <SumRow label="Grand Sub Total" amount={formatMoney(grandSubTotal)} variant="strong" />

              {/* Tax build-up — same grid, continues to the final Grand Total. */}
              {intra ? (
                <>
                  <SumRow label="CGST" amount={formatMoney(cgst)} />
                  <SumRow label="SGST" amount={formatMoney(sgst)} />
                </>
              ) : supplyType === 'inter_state' ? (
                <SumRow label="IGST" amount={formatMoney(igst)} />
              ) : (
                <SumRow label="GST" amount={formatMoney(0)} subnote="zero-rated" />
              )}
              <SumRow label="Round Off" amount={formatMoney(roundOffDisp)} />
              <SumRow label="Grand Total" amount={formatMoney(grand)} variant="grand" />
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
